<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black bg-opacity-50"
    @click="handleClickOutside"
  >
    <div
      class="relative w-full h-auto max-w-3xl p-6 overflow-y-auto bg-white rounded-lg"
      style="max-height: 90vh"
      @click.stop
    >
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold">{{ title }}</h2>
        <button
          type="button"
          data-bs-dismiss="modal"
          aria-label="Close"
          @click="closeModal"
          class="text-gray-500 transition duration-150 hover:text-gray-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <form
        class="space-y-6"
        @submit.prevent="submit"
        enctype="multipart/form-data"
      >
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <!-- Bagian Kiri: Data Dosen -->
          <div class="space-y-4">
            <InputField label="NIDN" icon="pen" v-model="form.nidn" />
            <InputError :message="form.errors.nidn" class="mt-2" />

            <InputField label="Nama" icon="pen" v-model="form.nama" />
            <InputError :message="form.errors.nama" class="mt-2" />

            <InputField label="NIP" icon="pen" v-model="form.nip" />
            <InputError :message="form.errors.nip" class="mt-2" />

            <InputField label="Email" icon="pen" v-model="form.email" />
            <InputError :message="form.errors.email" class="mt-2" />

            <InputField label="Nomor HP" icon="pen" v-model="form.nohp" />
            <InputError :message="form.errors.nohp" class="mt-2" />

            <InputField
              label="Link Dosen"
              icon="link"
              v-model="form.linkdosen"
              placeholder="https://... (mis. profil SINTA/Google Scholar)"
            />
            <InputError :message="form.errors.linkdosen" class="mt-2" />
          </div>

          <!-- Bagian Kanan: Pilihan Fakultas, Jurusan, Prodi, dan Foto -->
          <div class="space-y-4">
            <div>
              <label for="fakultas">Fakultas</label>
              <select
                v-model="form.fakultas_id"
                @change="getDepartemen"
                class="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Pilih Fakultas</option>
                <option
                  v-for="fakultas in fakultas.data"
                  :key="fakultas.id"
                  :value="fakultas.id"
                  :selected="fakultas.id == form.fakultas_id"
                >
                  {{ fakultas.name }}
                </option>
              </select>
              <InputError :message="form.errors.fakultas_id" class="mt-2" />
            </div>

            <div>
              <label for="departemen">Jurusan</label>
              <select
                v-model="form.departemen_id"
                @change="getProdi"
                class="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="" disabled>Pilih Jurusan</option>
                <option
                  v-for="departemen in localDepartemen"
                  :key="departemen.id"
                  :value="departemen.id"
                  :selected="departemen.id == form.departemen_id"
                >
                  {{ departemen.name }}
                </option>
              </select>
              <InputError :message="form.errors.departemen_id" class="mt-2" />
            </div>

            <div>
              <label for="prodi">Program Studi</label>
              <select
                v-model="form.prodi_id"
                class="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="" disabled>Pilih Program Studi</option>
                <option
                  v-for="prodi in localProdi"
                  :key="prodi.id"
                  :value="prodi.id"
                  :selected="prodi.id == form.prodi_id"
                >
                  {{ prodi.jenjang }} | {{ prodi.name }}
                </option>
              </select>
              <InputError :message="form.errors.prodi_id" class="mt-2" />
            </div>

            <div>
              <label for="foto">Foto Dosen</label>
              <input
                type="file"
                id="foto"
                accept="image/*"
                @change="onFileChange"
                class="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <InputError :message="form.errors.foto" class="mt-2" />
            </div>

            <!-- Tambahan: QuillEditor dan Jabatan Akademik -->
            <div>
              <label for="pendidikan">Pendidikan</label>
              <QuillEditor
                v-model:content="form.pendidikan"
                contentType="html"
                class="w-full bg-white"
              />
              <InputError :message="form.errors.pendidikan" class="mt-2" />
            </div>
            <div>
              <label for="jabakademik">Jabatan Akademik</label>
              <select
                v-model="form.jabakademik"
                id="jabakademik"
                class="block w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Pilih Jabatan Akademik</option>
                <option value="tenaga pengajar">Tenaga Pengajar</option>
                <option value="asisten ahli">Asisten Ahli</option>
                <option value="lektor">Lektor</option>
                <option value="lektor kepala">Lektor Kepala</option>
                <option value="guru besar">Guru Besar</option>
              </select>
              <InputError :message="form.errors.jabakademik" class="mt-2" />
            </div>
          </div>
        </div>
        <div class="flex justify-end space-x-4">
          <button
            type="button"
            @click="submit"
            class="px-4 py-2 mt-4 text-white bg-gray-800 rounded"
          >
            Simpan
          </button>
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 mt-4 text-white bg-red-500 rounded"
          >
            Tutup
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, watch, ref, reactive } from "vue";
import { useForm } from "@inertiajs/vue3";
import InputLabel from "./InputLabel.vue";
import InputError from "./InputError.vue";
import InputField from "./InputField.vue";
import { QuillEditor } from "@vueup/vue-quill";
import Swal from "sweetalert2";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  fakultas: {
    type: Object,
    required: true,
  },
  departemen: {
    type: Array,
    required: true,
  },
  prodi: {
    type: Array,
    required: true,
  },
  dosen: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["update:isOpen"]);

const form = useForm({
  id: null,
  nidn: "",
  nama: "",
  nip: "",
  email: "",
  nohp: "",
  fakultas_id: "",
  departemen_id: "",
  prodi_id: "",
  foto: null,
  pendidikan: "",
  jabakademik: "",
  linkdosen: "",
});
const localDepartemen = ref(props.departemen ? [...props.departemen] : []);
const localProdi = ref(props.prodi ? [...props.prodi] : []);
// Close modal function
const closeModal = () => {
  emit("update:isOpen", false);
};

// Handle clicking outside the modal to close it
const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

const getDepartemen = (event) => {
  form.fakultas_id = event.target.value;
  form.departemen_id = "";
  //   form.prodi_id = "";
  if (form.fakultas_id) {
    fetch(route("jurusan.show", form.fakultas_id))
      .then((response) => response.json())
      .then((data) => {
        localDepartemen.value = Array.isArray(data.data) ? data.data : [];
      })
      .catch((error) => {
        console.error(
          "There was an error fetching the departemen data:",
          error
        );
      });
  }
};
const getProdi = (event) => {
  form.departemen_id = event.target.value;
  form.prodi_id = "";
  if (form.departemen_id) {
    fetch(route("prodi.show", form.departemen_id))
      .then((response) => response.json())
      .then((data) => {
        localProdi.value = Array.isArray(data.data) ? data.data : [];
      })
      .catch((error) => {
        console.error("There was an error fetching the prodi data:", error);
      });
  }
};
watch(
  () => props.departemen,
  (newDepartemen) => {
    localDepartemen.value = [...newDepartemen];
  }
);
watch(
  () => props.dosen,
  (newDosen) => {
    if (newDosen) {
      form.id = newDosen.id;
      form.nidn = newDosen.nidn;
      form.nip = newDosen.nip;
      form.nama = newDosen.nama;
      form.email = newDosen.email;
      form.nohp = newDosen.nohp;
      form.fakultas_id = newDosen.fakultas.id;
      form.departemen_id = newDosen.departemen.id;
      form.prodi_id = newDosen.prodi.id;
      form.linkdosen = newDosen.linkdosen ?? "";
    }
  },
  { immediate: true, deep: true }
);

const onFileChange = (event) => {
  const file = event.target.files[0];
  if (file && file.type.startsWith("image/")) {
    form.foto = file;
  } else {
    form.foto = null;
    Swal.fire({
      title: "Error!",
      text: "File harus berupa gambar.",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};

const submit = () => {
  const routeName = form.id ? "dosen.update" : "dosen.store";
  form[form.id ? "put" : "post"](route(routeName, form.id || {}), {
    forceFormData: true,
    onSuccess: () => {
      form.reset();
      Swal.fire({
        title: "Berhasil!",
        text: `Data berhasil ${form.id ? "diperbarui" : "disimpan"}.`,
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        closeModal();
      });
    },
  });
};
</script>

<style scoped></style>
